import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './providers/AuthProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { SocketProvider } from './providers/SocketProvider';
import { QueryProvider } from './providers/QueryProvider';
import { RequireAuth } from './components/guards/RequireAuth';
import { RequirePermission } from './components/guards/RequirePermission';
import { VendorAuthProvider, RequireVendorAuth } from './components/guards/RequireVendorAuth';
import { AdminLayout } from './components/layout/AdminLayout';
import { VendorLayout } from './components/layout/VendorLayout';

// Admin pages
import { LoginPage } from './pages/admin/LoginPage';
import { ClientsPage } from './pages/admin/ClientsPage';
import { ClientDetailPage } from './pages/admin/ClientDetailPage';
import { ClientReportPage } from './pages/admin/ClientReportPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { MonitoringPage } from './pages/admin/MonitoringPage';
import { LogsPage } from './pages/admin/LogsPage';
import { AnalyticsPage } from './pages/admin/AnalyticsPage';
import { AlertRulesPage } from './pages/admin/AlertRulesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { IntegrationsPage } from './pages/admin/IntegrationsPage';
import { IntegrationDetailPage } from './pages/admin/IntegrationDetailPage';
import { PartnersPage } from './pages/admin/PartnersPage';
import { PartnerDetailPage } from './pages/admin/PartnerDetailPage';
import { RolesPage } from './pages/admin/RolesPage';
import { ProxyPage } from './pages/admin/ProxyPage';
import { ChangePasswordPage } from './pages/admin/ChangePasswordPage';
import { VendorsPage } from './pages/admin/VendorsPage';
import { VendorDetailAdminPage } from './pages/admin/VendorDetailPage';
import { NotFoundPage } from './pages/admin/NotFoundPage';

// Vendor pages
import { VendorLoginPage } from './pages/vendor/VendorLoginPage';
import { VendorDashboardPage } from './pages/vendor/VendorDashboardPage';
import { VendorDeviceDetailPage } from './pages/vendor/VendorDeviceDetailPage';

function AdminRoutes() {
  return (
    <AuthProvider>
      <SocketProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<RequireAuth />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to="/clients" replace />} />
              <Route path="/clients" element={<RequirePermission perm="clients"><ClientsPage /></RequirePermission>} />
              <Route path="/clients/:clientId" element={<RequirePermission perm="clients"><ClientDetailPage /></RequirePermission>} />
              <Route path="/clients/:clientId/report" element={<RequirePermission perm="clients"><ClientReportPage /></RequirePermission>} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/monitoring" element={<RequirePermission perm="monitoring"><MonitoringPage /></RequirePermission>} />
              <Route path="/logs" element={<RequirePermission perm="logs"><LogsPage /></RequirePermission>} />
              <Route path="/analytics" element={<RequirePermission perm="analytics"><AnalyticsPage /></RequirePermission>} />
              <Route path="/analytics/rules" element={<RequirePermission perm="analytics"><AlertRulesPage /></RequirePermission>} />
              <Route path="/settings" element={<RequirePermission perm="settings"><SettingsPage /></RequirePermission>} />
              <Route path="/users" element={<RequirePermission perm="users"><UsersPage /></RequirePermission>} />
              <Route path="/integrations" element={<RequirePermission perm="integrations"><IntegrationsPage /></RequirePermission>} />
              <Route path="/integrations/:id" element={<RequirePermission perm="integrations"><IntegrationDetailPage /></RequirePermission>} />
              <Route path="/partners" element={<RequirePermission perm="partners"><PartnersPage /></RequirePermission>} />
              <Route path="/partners/:id" element={<RequirePermission perm="partners"><PartnerDetailPage /></RequirePermission>} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/proxy" element={<RequirePermission perm="proxy"><ProxyPage /></RequirePermission>} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/vendors" element={<RequirePermission perm="vendors"><VendorsPage /></RequirePermission>} />
              <Route path="/vendors/:id" element={<RequirePermission perm="vendors"><VendorDetailAdminPage /></RequirePermission>} />
            </Route>
          </Route>
        </Routes>
      </SocketProvider>
    </AuthProvider>
  );
}

function VendorRoutes() {
  return (
    <VendorAuthProvider>
      <Routes>
        <Route path="login" element={<VendorLoginPage />} />
        <Route element={<RequireVendorAuth />}>
          <Route element={<VendorLayout />}>
            <Route path="dashboard" element={<VendorDashboardPage />} />
            <Route path="devices/:id" element={<VendorDeviceDetailPage />} />
          </Route>
        </Route>
      </Routes>
    </VendorAuthProvider>
  );
}

export default function App() {
  return (
    <QueryProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Vendor portal routes */}
            <Route path="/vendor/*" element={<VendorRoutes />} />

            {/* Admin routes (everything else) */}
            <Route path="/*" element={<AdminRoutes />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryProvider>
  );
}
